/** Qalam Shop TMS receipt formatting helpers. */

import {
  DEFAULT_RECEIPT_PAPER_SIZE,
  isReceiptPaperSize,
  type ReceiptPaperSize,
} from './thermalReceiptTypes.js';

export { RECEIPT_PAPER_SIZES, isReceiptPaperSize } from './thermalReceiptTypes.js';

/** Format amount like QalamShop TMS: `10,000` (comma decimal, 3 places). */
export function formatQalamReceiptAmount(amount: number): string {
  return amount.toFixed(3).replace('.', ',');
}

/** `RCP-20260829-0012` → `12` (display ticket number, no `N` prefix). */
export function formatQalamDisplayNumber(receiptId: string): string {
  const match = receiptId.match(/(\d+)\s*$/);
  if (!match) return receiptId;
  const n = parseInt(match[1], 10);
  return Number.isFinite(n) ? String(n) : receiptId;
}

/** @deprecated Use {@link formatQalamDisplayNumber} for printed tickets. */
export function formatQalamTicketLabel(receiptId: string): string {
  const n = formatQalamDisplayNumber(receiptId);
  return /^\d+$/.test(n) ? `N ${n}` : n;
}

export function normalizeQalamPaymentLabel(method: string): string {
  const upper = method.trim().toUpperCase();
  if (upper.startsWith('ESP') || upper === 'CASH') return 'ESPECE';
  if (upper.startsWith('CART') || upper === 'CARD') return 'CARTE';
  if (upper.startsWith('CHEQ') || upper === 'CHEQUE') return 'CHEQUE';
  return upper;
}

export type ReceiptLayoutColumns = {
  qty: number;
  article: number;
  unit: number;
  total: number;
};

export type ReceiptLayout = {
  paperSize: ReceiptPaperSize;
  lineWidth: number;
  columns: ReceiptLayoutColumns;
  /** ESC/POS GS v 0 bytes per raster row (dots / 8). */
  rasterBytesPerRow: number;
};

const LAYOUT_80MM: ReceiptLayout = {
  paperSize: '80mm',
  lineWidth: 48,
  columns: { qty: 3, article: 22, unit: 10, total: 13 },
  rasterBytesPerRow: 72,
};

const LAYOUT_58MM: ReceiptLayout = {
  paperSize: '58mm',
  lineWidth: 32,
  columns: { qty: 2, article: 14, unit: 8, total: 8 },
  rasterBytesPerRow: 48,
};

const LAYOUTS: Record<ReceiptPaperSize, ReceiptLayout> = {
  '80mm': LAYOUT_80MM,
  '58mm': LAYOUT_58MM,
};

/** 80 mm Font A — full printable width in characters (default profile). */
export const QALAM_RECEIPT_LINE_WIDTH = LAYOUT_80MM.lineWidth;

export const QALAM_RECEIPT_COLUMNS = LAYOUT_80MM.columns;

export function normalizeReceiptPaperSize(
  value: string | null | undefined,
): ReceiptPaperSize {
  return isReceiptPaperSize(value) ? value : DEFAULT_RECEIPT_PAPER_SIZE;
}

export function getReceiptLayout(
  paperSize?: ReceiptPaperSize | string | null,
): ReceiptLayout {
  return LAYOUTS[normalizeReceiptPaperSize(paperSize)];
}

function fitCell(
  text: string,
  width: number,
  align: 'left' | 'right',
): string {
  const clipped = text.length > width ? text.slice(0, width) : text;
  return align === 'right' ? clipped.padStart(width) : clipped.padEnd(width);
}

export function formatQalamTableHeader(
  paperSize?: ReceiptPaperSize | string | null,
): string {
  const { columns } = getReceiptLayout(paperSize);
  const { qty, article, unit, total } = columns;
  return (
    fitCell('Qt', qty, 'left') +
    fitCell('Article', article, 'left') +
    fitCell('Prix U.', unit, 'right') +
    fitCell('Montant', total, 'right')
  );
}

export function formatQalamProductDiscountRow(
  line: {
    productDiscountTtc: number;
    productDiscountPercentage?: number;
  },
  paperSize?: ReceiptPaperSize | string | null,
): string {
  const label =
    line.productDiscountPercentage && line.productDiscountPercentage > 0
      ? `  Remise -${line.productDiscountPercentage}%:`
      : '  Remise:';
  return padQalamReceiptLine(
    label,
    `-${formatQalamReceiptAmount(line.productDiscountTtc)}`,
    getReceiptLayout(paperSize).lineWidth,
  );
}

export function formatQalamCartDiscountRow(
  cart: {
    cartDiscountAmount: number;
    cartDiscountPercentage?: number;
  },
  paperSize?: ReceiptPaperSize | string | null,
): string {
  const label =
    cart.cartDiscountPercentage && cart.cartDiscountPercentage > 0
      ? `Remise panier -${cart.cartDiscountPercentage}%:`
      : 'Remise panier:';
  return padQalamReceiptLine(
    label,
    `-${formatQalamReceiptAmount(cart.cartDiscountAmount)}`,
    getReceiptLayout(paperSize).lineWidth,
  );
}

export function formatQalamItemRow(
  line: {
    name: string;
    quantity: number;
    unitPriceTtc: number;
    lineTotalTtc: number;
  },
  paperSize?: ReceiptPaperSize | string | null,
): string {
  const { columns } = getReceiptLayout(paperSize);
  const { qty, article, unit, total } = columns;
  return (
    fitCell(String(line.quantity), qty, 'left') +
    fitCell(line.name, article, 'left') +
    fitCell(formatQalamReceiptAmount(line.unitPriceTtc), unit, 'right') +
    fitCell(formatQalamReceiptAmount(line.lineTotalTtc), total, 'right')
  );
}

export function padQalamReceiptLine(
  left: string,
  right: string,
  width = QALAM_RECEIPT_LINE_WIDTH,
): string {
  if (left.length + right.length >= width) {
    return `${left.slice(0, Math.max(1, width - right.length - 1))} ${right}`;
  }
  return left + ' '.repeat(width - left.length - right.length) + right;
}
